import { format } from 'date-fns';

export const mapSearchBarClickedEventData = ({ inputValue, country }) => ({
  city: inputValue,
  country
});

export const mapCityClickedData = ({ slug, country }) => ({
  city: slug,
  country
});

export const mapOfferCardClickedEventData = ({
  id,
  availableFrom,
  price,
  currencyCode,
  rentBy,
  cardPosition,
  propertyType,
  page
}) => ({
  id: Number(id),
  offer_type: rentBy,
  price: price,
  currency: currencyCode,
  first_date_available: availableFrom,
  card_position: cardPosition,
  context: propertyType ? 'search' : 'homepage',
  ...(propertyType && { property_type: propertyType }),
  ...(page ? { page } : {})
});

export const mapTrackSignupEventData = ({ role, provider, referrer }) => ({
  user_type: role,
  signup_flow: getOrigin(referrer),
  signup_method: provider
});

export const mapViewSignupPage = ({ origin }) => ({
  signup_flow: getOrigin(origin)
});

export const mapSignupTabChanged = ({ tab }) => ({
  tab_changed: tab
});

export const mapFiltersAppliedEventData = ({
  exclusive: uniplaces_exclusive,
  ['housing-type']: housingType,
  ['rent-type']: place,
  ['instant-bookings']: isInstantBooking,
  ['min-response-rate']: minResponseRate,
  ['max-response-time']: maxResponseTime,
  ['has-resident-landlord']: hasResidentLandlord,
  ['restrictions-occupation']: occupationRestrictions,
  ['restrictions-gender']: genderRestrictions,
  ['restrictions-occupancy']: occupancyRestrictions,
  rules,
  bedrooms,
  bathrooms,
  ['ensuite']: typology_private_bathroom,
  ['bed-type']: bedType,
  ['offer-features']: offerFeatures,
  ['property-features']: propertyFeatures,
  ['has-trusted-landlord']: hasTrustedLandlord,
  ['has-checked-place']: hasCheckedPlace,
  ['move-in']: moveIn,
  ['move-out']: moveOut,
  ['flex-move-in']: flexMoveIn,
  ['flex-length']: flexLength,
  ['budget-min']: budgetMin,
  ['budget-max']: budgetMax,
  ['bills-included']: bills_included,
  guests,
  ['has-official-registration']: has_official_registration
}) => {
  const studentResidence = Array.isArray(housingType) && housingType.includes('residence') ? true : null;
  const notResident = hasResidentLandlord === 'false' ? 'does not live in the property' : null;
  const answersIn12Hours = maxResponseTime ? 'answers within 12h' : null;
  const highResponseRate = minResponseRate ? 'high response rate' : null;
  const instantBooking = isInstantBooking ? 'instant booking' : null;
  const landlord = [notResident, answersIn12Hours, highResponseRate, instantBooking].filter(item => item !== null);
  const trusted = hasTrustedLandlord ? 'trusted landlords' : null;
  const checked = hasCheckedPlace ? 'checked places' : null;
  const verification = [trusted, checked].filter(item => item !== null);
  const suitableFor = [
    ...(Array.isArray(occupationRestrictions) ? occupationRestrictions : []),
    ...(Array.isArray(occupancyRestrictions) ? occupancyRestrictions : []),
    ...(Array.isArray(rules) ? rules : [])
  ];

  let calendarMode = null;
  if (moveIn || moveOut || flexMoveIn || flexLength) {
    calendarMode = moveIn !== '' && moveOut !== '' ? 'specific date' : 'flexible';
  }

  const eventData = {
    uniplaces_exclusive,
    student_residence: studentResidence,
    place,
    landlord_filter: landlord.length ? landlord : undefined,
    gender_restriction: genderRestrictions,
    suitable_for: suitableFor.length ? suitableFor : undefined,
    typology_bedrooms: bedrooms ? bedrooms[0] : undefined,
    typology_bathrooms: bathrooms ? bathrooms[0] : undefined,
    typology_private_bathroom,
    bedroom: bedType || offerFeatures,
    house: propertyFeatures,
    verification: verification.length ? verification : undefined,
    move_in: moveIn,
    move_out: moveOut,
    flexible_move_in: flexMoveIn ? flexMoveIn[0] : undefined,
    flexible_length: flexLength,
    ...(calendarMode && { calendar_mode: calendarMode }),
    price_min: budgetMin / 100,
    price_max: budgetMax / 100,
    bills_included,
    guests,
    has_official_registration
  };

  return Object.entries(eventData).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {});
};

export const mapSortChangedEventData = ({ value }) => ({
  new_sort: value
});

export const mapUniTrustSearchClickedData = ({ source, url }) => ({
  source,
  url
});

export const mapOfferAlertCreatedEventData = ({ budgetMin, budgetMax, typology, destinationCity, source }) => ({
  budget_min: budgetMin > 0 ? budgetMin / 100 : budgetMin,
  budget_max: budgetMax / 100,
  typology: Array.isArray(typology) ? typology.join(', ') : typology,
  destination: destinationCity[0],
  source
});

export const mapSearchResultsSummaryData = ({
  searchTerm,
  searchCategory,
  moveIn,
  moveOut,
  count,
  source,
  filters,
  country
}) => ({
  search_term:
    searchTerm.includes('neighbourhood') || searchTerm.includes('university') ? searchTerm[2] : searchTerm[0],
  search_category: searchCategory,
  total_results: count,
  move_in: moveIn || null,
  move_out: moveOut || null,
  source,
  filters,
  country
});

export const mapQuickPageNavigation = ({ sectionName }) => ({
  section_name: sectionName.toLowerCase()
});

export const mapExtraGuestAdded = ({ newValue }) => ({
  guests: newValue
});

export const mapReportListingSubmittedEventData = ({ reason, offerId }) => ({
  reason: reason.replaceAll('_', ' '),
  offer_id: Number(offerId)
});

export const mapRequestToBookEventData = ({
  moveIn,
  moveOut,
  guests,
  optionId,
  offerId,
  price,
  currencyCode,
  availableFrom,
  propertyType,
  offerType,
  city,
  country,
  offerGenderRestriction,
  propertyGenderRestriction
}) => ({
  move_in: format(new Date(moveIn), 'yyyy-MM-dd'),
  move_out: format(new Date(moveOut), 'yyyy-MM-dd'),
  guests,
  ...(optionId && { fixed_contract_modal_open: optionId }),
  offer_id: offerId,
  price,
  currency: currencyCode,
  ...(availableFrom && { available_from: format(new Date(availableFrom), 'yyyy-MM-dd') }),
  property_type: propertyType,
  offer_type: offerType,
  city,
  country,
  offer_gender_restriction: offerGenderRestriction ?? [],
  property_gender_restriction: propertyGenderRestriction ?? []
});

export const mapDatesAppliedEventData = ({ moveIn, moveOut, source }) => ({
  move_in: format(new Date(moveIn), 'yyyy-MM-dd'),
  move_out: format(new Date(moveOut), 'yyyy-MM-dd'),
  context: source
});

export const mapQuickGalleryNavigation = ({ label }) => ({
  label: label.toLowerCase()
});

export const mapSharedOfferEventData = ({ method }) => ({
  method: method.toLowerCase()
});

export const mapViewOfferPage = ({ referrer, offerGenderRestriction, propertyGenderRestriction }) => ({
  page_view: referrer && getOrigin(referrer),
  offer_gender_restriction: offerGenderRestriction ?? [],
  property_gender_restriction: propertyGenderRestriction ?? []
});

export const mapVisitUnpublishedListingEventData = ({ offerId }) => ({
  offer_id: Number(offerId)
});

export const mapPriceReviewData = ({ context }) => ({
  context
});

export const mapAuthenticateWithVerificationCodeData = ({ data }) => ({
  ...(data && { result: data })
});

export const mapPromocodeVerificationEventData = ({ data }) => ({
  promo_code: data
});

const getOrigin = pathname => {
  if (pathname === 'hamburger menu') {
    return 'hamburger menu';
  }

  if (/\/accommodation\/[^/]+\/\d+/.test(pathname)) {
    return 'offer-page';
  }

  if (/\/accommodation\/[^/]/.test(pathname)) {
    return 'search';
  }

  if (pathname?.includes('booking') || pathname?.includes('checkout')) {
    return 'checkout';
  }

  if (pathname?.includes('/residences')) {
    return 'uniresidences';
  }

  return 'homepage';
};
